import {
  Show,
  useRecordContext,
  useCreatePath,
  RaRecord,
} from 'react-admin';
import { Link } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { SectionCard, Row } from '../shared/Panel';
import { wallClockDateTime } from '../shared/clientTime';
import { ViaWritePanel } from './ViaWritePanel';
import { viaDestinationLabel } from './viaWriteApi';
import {
  FEATURE_TMS_EXPORT_ONE_STEP,
  orderSourceLabel,
  viaFileStatusColor,
  viaFileStateName,
  viaMessageTypeLabel,
  viaWriteStatusColor,
  viaWriteStateName,
} from '../shared/status';
import { WriteDetails } from './WriteDetails';
import { PermissionGate, useHasPermission } from '../admin/PermissionGate';
import { ELEMENT } from '../admin/roleCenter/elementKeys';
import { useIntakeSide } from '../admin/intake/intakeSides';
import { useFeature } from '../admin/featureFlags/FeatureGate';

const WRITE_KIND_LABEL: Record<string, string> = {
  // Nazwy jak w panelu zapisu (16.10.2026): plik zlecenia i plik czynności
  // wózkowej (odpowiedzi Marathonu/Euvic, pyt. 39e).
  MAIN: 'Zlecenie',
  FORKLIFT: 'Czynność wózkowa',
  CUSTOMS: 'Odprawa celna',
};

// Rekordy zapisu do VIA — 1 zlecenie może dać serię rekordów TMS (np. + wózek).
const ViaWritesCard = ({ record }: { record: RaRecord }) => {
  // „Wysłany / zapisany w TMS” — przełącznik `tmsExportOneStep` (21.10.2026).
  const tmsNames = useFeature(FEATURE_TMS_EXPORT_ONE_STEP);
  const writes = (record.viaWrites as RaRecord[] | undefined) ?? [];
  return (
    <SectionCard title={`Rekordy zapisu do VIA / TMS (${writes.length})`}>
      {writes.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Brak rekordów — zlecenie nie zostało jeszcze wyeksportowane do VIA.
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: 'text.secondary' }}>Rodzaj</TableCell>
              <TableCell sx={{ color: 'text.secondary' }}>Plik XML</TableCell>
              <TableCell sx={{ color: 'text.secondary' }}>Status</TableCell>
              <TableCell sx={{ color: 'text.secondary' }}>Dokąd</TableCell>
              <TableCell sx={{ color: 'text.secondary' }}>Nr tour</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {writes.map((write) => (
              <TableRow key={write.id as number}>
                <TableCell sx={{ border: 0 }}>{WRITE_KIND_LABEL[write.recordKind as string] ?? (write.recordKind as string)}</TableCell>
                <TableCell sx={{ border: 0, color: 'text.secondary' }}>{(write.xmlFileName as string) ?? '—'}</TableCell>
                <TableCell sx={{ border: 0 }}>
                  {/* Kod w bazie, na ekranie słowo (07.10.2026 — szedł „GENERATED”). */}
                  <Chip size="small" color={viaFileStatusColor(write.status as string)} label={viaFileStateName(write.status as string, tmsNames)} />
                  {/* Komunikat błędu dostarczenia — bez danych wrażliwych, pisany dla operatora (15.10.2026). */}
                  {write.status === 'FAILED' && write.errorMessage ? (
                    <Typography variant="caption" color="error" sx={{ display: 'block', maxWidth: 360 }}>
                      {String(write.errorMessage)}
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell sx={{ border: 0, color: 'text.secondary' }}>
                  {viaDestinationLabel(write.destination as string | null | undefined)}
                </TableCell>
                <TableCell sx={{ border: 0 }}>{(write.viaTransportId as string) ?? '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </SectionCard>
  );
};

/**
 * Zlecenie z maila. Dane i rekordy zapisu widzi każdy zalogowany; panele
 * zapisu do VIA i danych dla importera tylko przyjęcie zleceń (01.10.2026):
 * zapis do TMS zatwierdza Administracja (Administracja §5.2), a FTL i Trade
 * mają podgląd bez edycji (§15). Komunikaty VIA też należą do przyjęcia
 * zleceń — bez uprawnienia nie ma odsyłacza do ekranu, który odpowie 403.
 */
const OrderShowLayout = () => {
  const record = useRecordContext();
  const createPath = useCreatePath();
  const canIntake = useHasPermission('ORDER_INTAKE');
  // Działy przyjęcia zleceń (fala B, 21.10.2026): zapis do VIA należy do
  // Administracji; bez przełącznika — wspólne przyjęcie zleceń, jak dotąd.
  const isAdministration = useIntakeSide('administration');
  const tmsNames = useFeature(FEATURE_TMS_EXPORT_ONE_STEP);
  if (!record) return null;

  const company = record.company as RaRecord | undefined;
  const preOrder = record.preOrder as RaRecord | undefined;
  const viaOrder = record.viaOrder as RaRecord | undefined;
  const bank = record.userOffer as RaRecord | undefined;
  const price = record.price != null ? `${record.price as number} ${(record.currency as string) ?? ''}`.trim() : '—';

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, p: 1, alignItems: 'start' }}>
      <Stack spacing={2}>
        <SectionCard title="Zlecenie">
          <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap' }} useFlexGap>
            <Chip size="small" color={viaWriteStatusColor(record.viaWriteStatus as string)} label={viaWriteStateName(record.viaWriteStatus as string, tmsNames)} />
            <Chip size="small" variant="outlined" label={orderSourceLabel(record.source as string)} />
            {record.responsibleDept ? <Chip size="small" variant="outlined" label={record.responsibleDept as string} /> : null}
          </Stack>
          <Table size="small">
            <TableBody>
              <Row label="Nr zlecenia">{(record.orderNumber as string) ?? `#${record.id as number}`}</Row>
              <Row label="Klient">{(record.clientName as string) ?? '—'}</Row>
              {/* Numer od klienta idzie do VIA zamiast naszego (16.10.2026, pyt. 27). */}
              <Row label="Nr zamówienia klienta">{(record.customerOrderNumber as string) || '—'}</Row>
              <Row label="Wymagany typ pojazdu">{(record.requiredVehicleType as string) || '—'}</Row>
              <Row label="Relacja">{(record.relation as string) ?? '—'}</Row>
              <Row label="Załadunek">{(record.loadingCity as string) ?? '—'} · {wallClockDateTime(record.loadingDate as string)}</Row>
              <Row label="Rozładunek">{(record.unloadingCity as string) ?? '—'} · {wallClockDateTime(record.unloadingDate as string)}</Row>
              <Row label="Waga">{record.weight != null ? `${record.weight as number} kg` : '—'}</Row>
              <Row label="Cena">{price}</Row>
              <Row label="Spółka">{(company?.name as string | undefined) || (company?.viaCompanyId as string | undefined) || '—'}</Row>
              {/*
                Ten sam transport w banku zleceń — stan planowania i obsada.
                Bez tego odsyłacza ze zlecenia nie dało się dojść do pracy
                planisty inaczej niż przez prezlecenie (W4/W12).
              */}
              <Row label="W banku zleceń">
                {bank ? (
                  <Button
                    size="small"
                    startIcon={<OpenInNewIcon />}
                    component={Link}
                    to={createPath({ resource: 'user-offers', type: 'show', id: bank.id as number })}
                  >
                    {(bank.externalId as string) ?? `#${bank.id as number}`}
                    {bank.assignedCar
                      ? ` · ${((bank.assignedCar as Record<string, unknown>).registrationNumber as string) ?? ''}`
                      : ''}
                  </Button>
                ) : 'jeszcze nie ma wiersza'}
              </Row>
              <Row label="Prezlecenie">
                {preOrder ? (
                  <Button size="small" startIcon={<OpenInNewIcon />} component={Link} to={createPath({ resource: 'pre-orders', type: 'show', id: preOrder.id as number })}>
                    Prezlecenie #{preOrder.id as number}
                  </Button>
                ) : '—'}
              </Row>
            </TableBody>
          </Table>
        </SectionCard>

        <SectionCard title="Zlecenie w VIA (odczyt / W11)">
          {viaOrder ? (
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }} useFlexGap>
              <Typography variant="body2">
                {(viaOrder.orderNumber as string) ?? (viaOrder.transportId as string)} · {(viaOrder.startCity as string) ?? '?'} → {(viaOrder.endCity as string) ?? '?'}
              </Typography>
              <Chip size="small" variant="outlined" label={viaMessageTypeLabel(viaOrder.messageType as string)} />
              {canIntake && (
                <Button size="small" startIcon={<OpenInNewIcon />} component={Link} to={createPath({ resource: 'via-orders', type: 'show', id: viaOrder.id as number })}>
                  Otwórz w VIA
                </Button>
              )}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Nie dopasowano jeszcze do odczytu z VIA — po dostarczeniu pliku i re-odczycie zlecenie zostanie oznaczone „jest w TMS".
            </Typography>
          )}
        </SectionCard>

        <PermissionGate permission="ORDER_INTAKE" element={ELEMENT.orderFromMailWriteDetails}>
          <WriteDetails />
        </PermissionGate>
      </Stack>

      <Stack spacing={2}>
        <ViaWritesCard record={record} />

        <PermissionGate permission="ORDER_INTAKE" element={ELEMENT.orderFromMailViaWrite}>
          {isAdministration ? (
            <ViaWritePanel />
          ) : (
            <Alert severity="info" data-testid="via-write-administration-only">
              Zatwierdzenie i plik dla VIA należą do Administracji. Braki, które blokują plik, widać w obiegu
              prezlecenia i w „Danych dla importera”.
            </Alert>
          )}
        </PermissionGate>
      </Stack>
    </Box>
  );
};

export const OrderShow = () => (
  <Show>
    <OrderShowLayout />
  </Show>
);
